import React, { useRef, useEffect } from 'react';
import { Cpu, Cloud, Database, Smartphone } from 'lucide-react';

const TechnologyStack = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    // Network nodes animation
    const nodes = [];
    const nodeCount = 50;

    for (let i = 0; i < nodeCount; i++) {
      nodes.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: 2,
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw nodes
      nodes.forEach((node) => {
        node.x += node.vx;
        node.y += node.vy;

        if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.height) node.vy *= -1;

        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 128, 0, 0.6)';
        ctx.fill();
      });

      // Draw connections
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 100) {
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(255, 128, 0, ${0.2 - distance / 500})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const technologies = [
    {
      icon: Cpu,
      category: 'AR & 3D Engine',
      items: ['Unity & WebGL', 'ARKit & ARCore', 'Real-time rendering', 'Mobile optimization'],
    },
    {
      icon: Cloud,
      category: 'Cloud Platform',
      items: ['AWS infrastructure', 'Real-time sync', 'Scalable storage', 'Fast delivery'],
    },
    {
      icon: Database,
      category: 'AI & Data',
      items: ['Design AI models', 'Price intelligence', 'Product database', 'Pattern recognition'],
    },
    {
      icon: Smartphone,
      category: 'Marketplace',
      items: ['E-commerce platform', 'Partner integration', 'Payment processing', 'Order tracking'],
    },
  ];

  const integrations = [
    'iOS ARKit',
    'Android ARCore',
    'React Native',
    'Unity',
    'Blender',
    'SketchUp',
    'AutoCAD',
    'Stripe Payments',
    'Shopify',
    'WooCommerce',
    'Delivery APIs',
    'Partner Network',
  ];

  return (
    <section id="technology" className="relative py-32 px-6 md:px-[7.6923%] overflow-hidden">
      {/* Animated background */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ opacity: 0.3 }}
      />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-20">
          <h2
            className="text-4xl md:text-5xl lg:text-6xl font-semibold mb-6"
            style={{
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em',
            }}
          >
            Technology Stack
          </h2>
          <p
            className="text-lg md:text-xl max-w-3xl mx-auto"
            style={{ color: 'var(--text-secondary)' }}
          >
            Built on enterprise-grade infrastructure with cutting-edge spatial computing technology.
          </p>
        </div>

        {/* Technology grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {technologies.map((tech, index) => (
            <div
              key={index}
              className="glass p-6 rounded-2xl border-2 border-orange-200 hover:border-orange-400 transition-all duration-400 hover:shadow-xl"
            >
              <div className="mb-4">
                <tech.icon size={40} color="#FF8000" strokeWidth={1.5} />
              </div>
              <h3
                className="text-xl font-semibold mb-4"
                style={{ color: 'var(--text-primary)' }}
              >
                {tech.category}
              </h3>
              <ul className="space-y-2">
                {tech.items.map((item, idx) => (
                  <li
                    key={idx}
                    className="flex items-center gap-2 text-sm"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    <span
                      className="w-1.5 h-1.5 flex-shrink-0"
                      style={{ backgroundColor: 'var(--brand-primary)' }}
                    />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Integrations */}
        <div className="glass p-8 md:p-12 rounded-3xl border-2 border-orange-200 hover:border-orange-400">
          <h3
            className="text-2xl md:text-3xl font-semibold mb-8 text-center"
            style={{ color: 'var(--text-primary)' }}
          >
            Compatible With Industry Standards
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            {integrations.map((integration, index) => (
              <div
                key={index}
                className="px-6 py-3 border border-[#CCCCCC] rounded-full transition-all duration-300 hover:border-[#FF8000] hover:bg-orange-50"
              >
                <span className="text-base font-medium" style={{ color: 'var(--text-secondary)' }}>
                  {integration}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechnologyStack;